import { useEffect } from "react";
import { motion } from "framer-motion";
import { Heart, Shield, Globe, Users } from "lucide-react";

const AboutPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen py-8 sm:py-12 md:py-20 px-4 sm:px-6 md:px-12 lg:px-24" data-testid="about-page">
      <div className="max-w-4xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          {/* Header */}
          <div className="text-center mb-8 sm:mb-12">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#E8A0BF]/10 rounded-full text-sm text-[#C25E5E] mb-4">
              <Heart className="w-4 h-4 fill-[#C25E5E]" />
              <span>Our Story</span>
            </div>
            <h1 
              className="text-2xl sm:text-3xl md:text-4xl font-bold text-[#2D2D2D] mb-4"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              About LoBe
            </h1>
            <p className="text-sm sm:text-base text-[#5A5A5A] max-w-2xl mx-auto">
              Creating fun, novelty certificates since 2026
            </p>
          </div>

          {/* Main Content */}
          <div className="prose prose-lg max-w-none">
            <div className="bg-white rounded-xl sm:rounded-2xl shadow-lg p-5 sm:p-8 md:p-12 border border-[#E2E2E2]/50 mb-6 sm:mb-8">
              <h2 className="text-xl sm:text-2xl font-semibold text-[#2D2D2D] mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
                Our Mission
              </h2>
              <p className="text-sm sm:text-base text-[#5A5A5A] leading-relaxed mb-4 sm:mb-6">
                LoBe was created to bring a smile to people's faces. We wanted to create a fun way 
                for people to celebrate their connections with unique, beautifully designed novelty 
                certificates.
              </p>
              <p className="text-sm sm:text-base text-[#5A5A5A] leading-relaxed mb-4 sm:mb-6">
                Our platform allows you to create a fun certificate with your names and a personal 
                message. Whether for a special occasion, a joke among friends, or just for fun, 
                LoBe provides the perfect canvas for your creativity.
              </p>
              <p className="text-sm sm:text-base text-[#5A5A5A] leading-relaxed">
                Each certificate is carefully designed with beautiful aesthetics, featuring elegant 
                typography, soft colors, and meaningful details. Download your high-quality PNG 
                certificate instantly and share the fun!
              </p>
            </div>

            {/* Values */}
            <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-4 sm:gap-6 mb-6 sm:mb-8">
              <div className="bg-white rounded-xl sm:rounded-2xl shadow-lg p-5 sm:p-6 border border-[#E2E2E2]/50">
                <Shield className="w-8 h-8 sm:w-10 sm:h-10 text-[#B4D2E7] mb-3 sm:mb-4" />
                <h3 className="text-base sm:text-lg font-semibold text-[#2D2D2D] mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>
                  Secure & Private
                </h3>
                <p className="text-xs sm:text-sm text-[#5A5A5A]">
                  Your data is protected. We never share your personal information.
                </p>
              </div>
              
              <div className="bg-white rounded-xl sm:rounded-2xl shadow-lg p-5 sm:p-6 border border-[#E2E2E2]/50">
                <Heart className="w-8 h-8 sm:w-10 sm:h-10 text-[#E8A0BF] mb-3 sm:mb-4" />
                <h3 className="text-base sm:text-lg font-semibold text-[#2D2D2D] mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>
                  Made for Fun
                </h3>
                <p className="text-xs sm:text-sm text-[#5A5A5A]">
                  Every certificate is crafted with care for your entertainment and enjoyment.
                </p>
              </div>
              
              <div className="bg-white rounded-xl sm:rounded-2xl shadow-lg p-5 sm:p-6 border border-[#E2E2E2]/50 sm:col-span-2 md:col-span-1">
                <Globe className="w-8 h-8 sm:w-10 sm:h-10 text-[#D4AF37] mb-3 sm:mb-4" />
                <h3 className="text-base sm:text-lg font-semibold text-[#2D2D2D] mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>
                  Global Reach
                </h3>
                <p className="text-xs sm:text-sm text-[#5A5A5A]">
                  Our platform serves users from around the world with localized pricing.
                </p>
              </div>
            </div>

            {/* Important Notice */}
            <div className="bg-amber-50 border-2 border-amber-200 rounded-xl sm:rounded-2xl p-5 sm:p-8 mb-6 sm:mb-8">
              <h3 className="text-lg sm:text-xl font-semibold text-amber-800 mb-3 sm:mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
                Important Notice
              </h3>
              <p className="text-sm sm:text-base text-amber-700">
                LoBe certificates are for <strong>entertainment and novelty purposes only</strong>. 
                They have no legal validity and are not official documents. 
                They cannot be used for any legal, official, or formal purposes.
              </p>
            </div>

            {/* Contact */}
            <div className="bg-[#F4F1EA] rounded-xl sm:rounded-2xl p-5 sm:p-8 text-center">
              <Users className="w-8 h-8 sm:w-10 sm:h-10 text-[#E8A0BF] mx-auto mb-3 sm:mb-4" />
              <h3 className="text-lg sm:text-xl font-semibold text-[#2D2D2D] mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>
                Get in Touch
              </h3>
              <p className="text-sm sm:text-base text-[#5A5A5A] mb-3 sm:mb-4">
                Have questions or feedback? We'd love to hear from you.
              </p>
              <a 
                href="mailto:support@lobe.love" 
                className="text-[#E8A0BF] hover:text-[#D68C85] font-medium transition-colors text-sm sm:text-base"
              >
                support@lobe.love
              </a>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default AboutPage;
